/**
 * The paper plan (issue #49): Vaktplan and Min plan print a clean table through the browser's own
 * print, styled by the `@media print` block in styles.css. The sheet is a portal on <body>, so the
 * print rule hides every other child of <body> (the shell, the toolbar, the launcher) at once. It
 * is mounted only while printing, so the screen and its tests never see a second copy of the plan.
 */
import { T } from '@/i18n';
import { oslo, today } from '@/rules';
import { addDays, dow, isoWeek } from '@timefly/core';
import { useEffect, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { DN, fmtD, fmtDs, num } from './format';

/**
 * One line of the table: a name and the lines it shows on each day. #200: `notes` are said in italics
 * under the shifts (an absence, a day off); `hours` per day fills the Timer column and the total row;
 * `heading` is a department's line across the table (#199), with nothing else on it.
 */
export interface PrintRow {
  key: string | number;
  name: string;
  cells: Map<string, string[]>;
  notes?: Map<string, string[]>;
  hours?: Map<string, number>;
  heading?: boolean;
}

const tenth = (n: number) => T('{h} t', { h: num(Math.round(n * 10) / 10) });

/** "08:00-16:00 Dag", with "(utkast)" on a shift that is not published. */
export const shiftLine = (
  s: { start_time: string; end_time: string; published?: boolean | null },
  type: string | undefined,
) =>
  [
    `${oslo(s.start_time)?.time ?? ''}-${oslo(s.end_time)?.time ?? ''}`,
    type ?? '',
    s.published === false ? `(${T('Utkast').toLocaleLowerCase('nb')})` : '',
  ]
    .filter(Boolean)
    .join(' ');

/** Days grouped by ISO week, in order: one table and one page each. */
const weeksOf = (days: string[]) => {
  const out: string[][] = [];
  for (const d of days) {
    const last = out[out.length - 1];
    if (last && isoWeek(last[0] as string) === isoWeek(d) && d <= addDays(last[0] as string, 6)) last.push(d);
    else out.push([d]);
  }
  return out;
};

/**
 * `print()` mounts the sheet and calls window.print(); Ctrl+P mounts it through beforeprint.
 * afterprint takes it away again.
 */
export function usePrint() {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const before = () => flushSync(() => setOn(true));
    const after = () => setOn(false);
    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => {
      window.removeEventListener('beforeprint', before);
      window.removeEventListener('afterprint', after);
    };
  }, []);
  const print = () => {
    flushSync(() => setOn(true));
    window.print();
  };
  return { on, print };
}

export function PrintSheet({
  title,
  company,
  days,
  rows,
  portrait,
}: { title: string; company: string; days: string[]; rows: PrintRow[]; portrait?: boolean }) {
  const printed = T('Skrevet ut {date}', { date: fmtD(today()) });
  // the Timer column and the total row, when the rows carry hours (Vaktplan, #200)
  const timed = rows.some((r) => r.hours);
  const sum = (r: PrintRow, w: string[]) => w.reduce((a, d) => a + (r.hours?.get(d) ?? 0), 0);
  const cols = (w: string[]) => w.length + 1 + (timed ? 1 : 0);
  return createPortal(
    <div className="printsheet" data-print="plan" data-size={portrait ? 'portrait' : 'landscape'}>
      {/* the paper size lives here, so it applies only while the plan prints (a personalliste keeps
          the browser's own). Not a named page: Chromium closed a named page with a blank one
          (measured 2026-10-12, 2 PDF pages for one week, 1 without the name). */}
      <style>{`@page{size:A4 ${portrait ? 'portrait' : 'landscape'};margin:10mm}`}</style>
      {weeksOf(days).map((w) => (
        <table className="pweek" key={w[0]}>
          {/* thead and tfoot repeat on every page a long week runs over */}
          <thead>
            <tr className="phead">
              <th colSpan={cols(w)}>
                <b>{title}</b>
                <span>{company}</span>
                <span>
                  {T('Uke {n}', { n: isoWeek(w[0] as string) })}, {fmtDs(w[0] as string)}-
                  {fmtD(w[w.length - 1] as string)}
                </span>
              </th>
            </tr>
            <tr>
              <th className="pname">{T('Navn')}</th>
              {w.map((d) => (
                <th key={d}>
                  {DN[dow(d)]} {fmtDs(d)}
                </th>
              ))}
              {timed && <th className="phrs">{T('Timer')}</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) =>
              r.heading ? (
                <tr key={r.key} className="pdep">
                  <th colSpan={cols(w)}>{r.name}</th>
                </tr>
              ) : (
                <tr key={r.key}>
                  <th scope="row" className="pname">
                    {r.name}
                  </th>
                  {w.map((d) => (
                    <td key={d}>
                      {(r.cells.get(d) ?? []).map((line, i) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: two equal lines on one day are two shifts
                        <div key={i}>{line}</div>
                      ))}
                      {(r.notes?.get(d) ?? []).map((line, i) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: a note is said once a day, its place is its key
                        <div key={`n${i}`} className="pabs">
                          {line}
                        </div>
                      ))}
                    </td>
                  ))}
                  {timed && <td className="phrs">{r.hours ? tenth(sum(r, w)) : ''}</td>}
                </tr>
              ),
            )}
            {timed && (
              <tr className="ptot">
                <th scope="row" className="pname">
                  {T('Timer')}
                </th>
                {w.map((d) => (
                  <td key={d} className="phrs">
                    {tenth(rows.reduce((a, r) => a + (r.hours?.get(d) ?? 0), 0))}
                  </td>
                ))}
                <td className="phrs">{tenth(rows.reduce((a, r) => a + sum(r, w), 0))}</td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="pfoot">
              <td colSpan={cols(w)}>{printed}</td>
            </tr>
          </tfoot>
        </table>
      ))}
    </div>,
    document.body,
  );
}
